import { Settings2, LogOut, Timer, Gift } from "lucide-react";
import { useState } from "react";
import NotificationBell from "@/components/NotificationBell";
import ReferralModal from "@/components/ReferralModal";

function remainingLabel(iso) {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "Scaduto";
  const days = Math.floor(ms / 86400000);
  if (days >= 1) return `${days}g rimanenti`;
  const hours = Math.floor(ms / 3600000);
  if (hours >= 1) return `${hours}h rimanenti`;
  return `${Math.max(1, Math.floor(ms / 60000))}min rimanenti`;
}

function remainingShort(iso) {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "Scad.";
  const days = Math.floor(ms / 86400000);
  if (days >= 1) return `${days}g`;
  const hours = Math.floor(ms / 3600000);
  if (hours >= 1) return `${hours}h`;
  return `${Math.max(1, Math.floor(ms / 60000))}m`;
}

const TABS = [
  { id: "calcolo", label: "Fase Calcolo", testId: "nav-tab-calcolo" },
  { id: "riepilogo", label: "Riepilogo", testId: "nav-tab-riepilogo" },
  { id: "movimenti", label: "Movimenti", testId: "nav-tab-movimenti" },
  { id: "storico", label: "Storico", testId: "nav-tab-storico" },
];

export default function TerminalHeader({ auth, tab, setTab, hasAnalysis, movimentiUnlocked, onLogout, onOpenAdmin, onOpenDisclaimer }) {
  const [referralOpen, setReferralOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/95 border-b border-[#E3BC55]/25">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-12">
          <div className="flex items-center">
            <img src="/logo.png" alt="Zero Signals" data-testid="header-logo" className="h-7 sm:h-8 w-auto object-contain" />
          </div>
          <div className="flex items-center gap-2">
            <span data-testid="session-code-badge" className="font-mono-data text-[11px] text-[#E3BC55]/90 border border-[#E3BC55]/25 rounded-sm px-2 py-1 hidden sm:block">
              {auth.code} {auth.role === "admin" ? "· ADMIN" : "· Device #1"}
            </span>
            {auth.role !== "admin" && (
              <span
                data-testid="code-expiry-badge"
                className="flex items-center gap-1 font-mono-data text-[11px] text-emerald-400/90 border border-emerald-500/25 rounded-sm px-1.5 py-1"
                title={auth.codeExpiresAt ? `Il tuo codice scade il ${new Date(auth.codeExpiresAt).toLocaleString("it-IT")}` : "Il tuo codice non ha scadenza"}
              >
                <Timer className="w-3 h-3 shrink-0" />
                <span className="sm:hidden">{auth.codeExpiresAt ? remainingShort(auth.codeExpiresAt) : "∞"}</span>
                <span className="hidden sm:inline">{auth.codeExpiresAt ? remainingLabel(auth.codeExpiresAt) : "Permanente"}</span>
              </span>
            )}
            <button
              data-testid="disclaimer-dot-button"
              onClick={onOpenDisclaimer}
              title="Informativa e scarico di responsabilità (accettata)"
              className="w-5 h-5 rounded-full border border-[#E3BC55]/50 flex items-center justify-center hover:border-[#FFD700] transition-colors"
            >
              <span className="w-2 h-2 rounded-full bg-[#E3BC55] pulse-dot" />
            </button>
            <NotificationBell />
            {auth.role !== "admin" && (
              <button
                data-testid="referral-trigger-button"
                onClick={() => setReferralOpen(true)}
                className="flex items-center gap-1.5 text-xs font-heading uppercase tracking-wider text-[#E3BC55] border border-[#E3BC55]/40 hover:border-[#E3BC55] hover:text-[#FFD700] rounded-sm px-2.5 py-1.5 transition-colors"
                title="Invita un amico e guadagna giorni gratis"
              >
                <Gift className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Invita</span>
              </button>
            )}
            {auth.role === "admin" && (
              <button
                data-testid="admin-trigger-button"
                onClick={onOpenAdmin}
                className="flex items-center gap-1.5 text-xs font-heading uppercase tracking-wider text-black bg-[#E3BC55] hover:bg-[#FFD700] rounded-sm px-3 py-1.5 transition-colors"
              >
                <Settings2 className="w-3.5 h-3.5" /> Admin
              </button>
            )}
            {auth.role === "admin" && (
              <button
                data-testid="logout-button"
                onClick={onLogout}
                className="text-slate-500 hover:text-red-400 transition-colors p-1.5"
                title="Disconnetti"
              >
                <LogOut className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        <nav className="flex -mb-px overflow-x-auto">
          {TABS.map((t) => {
            const locked = (t.id === "riepilogo" && !hasAnalysis) || (t.id === "movimenti" && !movimentiUnlocked);
            return (
              <button
                key={t.id}
                data-testid={t.testId}
                disabled={locked}
                onClick={() => setTab(t.id)}
                className={`whitespace-nowrap px-5 py-3 text-sm sm:text-base font-heading font-bold uppercase tracking-widest border-b-[3px] transition-colors ${
                  tab === t.id
                    ? "border-[#E3BC55] text-[#E3BC55]"
                    : locked
                    ? "border-transparent text-slate-700 cursor-not-allowed"
                    : "border-transparent text-slate-400 hover:text-[#E3BC55]"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>
      {referralOpen && <ReferralModal code={auth.code} onClose={() => setReferralOpen(false)} />}
    </header>
  );
}
